import { useCallback, useContext, useRef } from 'react';
import moment from 'moment';
import { ColDef, GridApi } from 'ag-grid-community';

import { toCsv } from 'components/DataTable/exportCells';
import { PACE_CELLS } from 'components/PaceMatrix/PaceMatrixContent/constants';

import CardContext from '../../../contexts/CardContext';
import GridContext from '../../../contexts/GridContext';
import getComparison from '../../V5Gadget/Matrix/getComparison';
import { HISTOGRAM_CELL_RENDERER } from '../../SingleMetricDateMatrix/SingleMetricDateMatrixContent/constants';
import delay from '../../../delay';
import { getProgressCellText } from '../../ProgressCellRenderer';
import { getContractProgressCellText } from '../../ContractProgressCell';
import { DATE_CELL, GEO_POINT_CELL } from '../constants';
import useDateScope from '../../../hooks/useDateScope';
import useValueFormatters from 'hooks/useValueFormatters';

interface FleetOpsGridColDef extends ColDef {
  cellRendererParams:
    | {
        fieldView?: FleetOps.BaseViewField;
        cell?: BoardProgressCell;
        // Pace cells
        metricId?: string;
      }
    | undefined;
}

type ValueFormatters = ReturnType<typeof useValueFormatters>;

export const processCellCallback =
  ({
    formatValue,
    formatField,
    formatMetric,
  }: Pick<ValueFormatters, 'formatValue' | 'formatField' | 'formatMetric'>) =>
  // AG's ProcessCellForExportParams, or DataTable's equivalent
  (params: DataTable.ExportCellParams) => {
    const colDef = params.column.getUserProvidedColDef() as
      | FleetOpsGridColDef
      | undefined;
    if (!colDef) {
      return '';
    }
    const cellRenderer = colDef.cellRenderer;
    const cellParams = colDef.cellRendererParams;

    if (cellRenderer === 'progressCell' && params.node) {
      if (!cellParams) {
        return '';
      }
      if (!cellParams.cell) {
        return '';
      }

      const cell = cellParams.cell as BoardProgressCell;
      const fieldView = cellParams.fieldView as
        | FleetOps.BaseViewField
        | undefined;
      return getProgressCellText(
        params.node.data,
        cell,
        formatValue,
        fieldView,
      );
    }

    if (cellRenderer === 'contractProgressCell' && params.node) {
      return getContractProgressCellText(params.node.data, formatValue);
    }

    // Pace cells as the table shows them. A change has no arrow here, so a
    // rise is marked with a plus
    if (
      cellParams &&
      cellParams.metricId &&
      (cellRenderer === PACE_CELLS.CELL || cellRenderer === PACE_CELLS.DIFF)
    ) {
      if (typeof params.value !== 'number') {
        return '-';
      }
      const formatted = formatMetric({
        metricId: cellParams.metricId,
        value: params.value,
      });
      return cellRenderer === PACE_CELLS.DIFF && params.value >= 0
        ? `+ ${formatted}`
        : formatted;
    }

    if (
      cellParams &&
      cellRenderer === DATE_CELL &&
      cellParams.fieldView &&
      cellParams.fieldView.dateFormat
    ) {
      if (params.value) {
        return moment.utc(params.value).format(cellParams.fieldView.dateFormat);
      }
      return '';
    }

    if (cellParams && cellRenderer === GEO_POINT_CELL && cellParams.fieldView) {
      return formatField({
        value: params.value,
        field: cellParams.fieldView.field,
        dataset: cellParams.fieldView.dataset,
      });
    }

    if (params.value !== undefined && Array.isArray(params.value)) {
      return params.value.join(', ');
    }

    if (typeof params.value === 'number') {
      if (
        cellRenderer === 'currencyCell' ||
        cellRenderer === 'bonusRewardCell'
      ) {
        const formatted = formatValue({
          value: params.value,
          formatting: {
            precision: 2,
          },
        });
        return formatted;
      }
    }

    if (params.value && typeof params.value === 'object') {
      const { value, formatting, type } = params.value;
      if (!params.value) {
        return '-';
      }

      const precision = formatting ? formatting.precision : 2;
      const prefix = formatting ? formatting.prefix : undefined;
      const postfix = formatting ? formatting.postfix : undefined;

      const updatedFormatting = { precision, prefix, postfix };

      const formatted = formatValue({ value, formatting: updatedFormatting });
      const previous =
        cellRenderer === HISTOGRAM_CELL_RENDERER
          ? params.value.comparison
          : params.value.previous;
      if (type === 'percentDelta' || type === 'delta') {
        if (previous === 0) {
          return type === 'percentDelta' ? '' : formatted;
        }
        const { delta } = getComparison({
          value: value as number,
          previous: previous as number,
          formatting,
          type,
        });
        if (delta === Infinity) {
          return '';
        }

        if (delta === null || delta === undefined) {
          return '-';
        }

        // A percentage, whatever the metric's own units
        if (type === 'percentDelta') {
          return `${delta >= 0 ? '+' : '-'} ${Math.abs(delta)}%`;
        }

        if (delta >= 0) {
          return `+ ${formatValue({ value: delta, formatting })}`;
        } else if (prefix !== 'currency') {
          return `- ${formatValue({ value: Math.abs(delta), formatting })}`;
        }

        return `${formatValue({ value: delta, formatting })}`;
      } else {
        return formatted;
      }
    }

    if (typeof params.value === 'string') {
      return params.value.replace(
        /[^a-zA-Z0-9, !@#$%^&*()\-=_+`.;:'"?|[\]\\/{}]/g,
        ' ',
      );
    }
    return params.value;
  };

// Encapsulates the CSV generation for a grid gadget (ag-grid `getDataAsCsv`),
// including the filename derivation and the cell formatting callback.
// Extracted from ExportButton so it can be reused by GridCsvExportRegistrar,
// which registers a matrix gadget's CSV during headless scheduled-report export
// without the export ContextMenu ever being opened.
const useGridCsvExport = ({
  overrideGridApi,
  exportHandle,
  exportName,
}: {
  overrideGridApi?: GridApi;
  // A DataTable's rows instead of a grid
  exportHandle?: DataTable.ExportHandle;
  exportName?: string;
}) => {
  const { i } = useContext(CardContext);
  const { gridApi, dataType } = useContext(GridContext);
  const dateScope = useDateScope({});
  const { formatValue, formatField, formatMetric } = useValueFormatters();
  const domId = `export-button-${i}`;

  const getFileName = useCallback(() => {
    const name = exportName ? exportName : dataType;
    const prefix = `FleetOps ${name} export`;
    const postfix = `${dateScope.startDate} - ${dateScope.endDate}`;
    const fileName = `${prefix} - ${postfix}`.replaceAll('.', '');
    return fileName;
  }, [dataType, dateScope.endDate, dateScope.startDate, exportName]);

  // Read when exporting, as the AG path reads the grid then: a callback
  // registered before the table's last update (e.g. its totals arriving
  // after its rows) still exports what the table shows
  const exportHandleRef = useRef(exportHandle);
  exportHandleRef.current = exportHandle;

  const exportDataAsCsv = useCallback(async (): Promise<ExcelFileData> => {
    const handle = exportHandleRef.current;
    if (handle) {
      // Built from the table's rows, so there's nothing to wait for
      const fileName = getFileName();
      const csv = toCsv(
        handle.getCells({
          processCell: processCellCallback({
            formatValue,
            formatField,
            formatMetric,
          }),
          isTotalsRowIncluded: true,
        }),
      );
      return { content: csv.replace(/€/g, ''), fileName };
    }
    if (!gridApi && !overrideGridApi) {
      throw new Error('Grid API missing');
    }
    const gridApiToUse = overrideGridApi
      ? overrideGridApi
      : (gridApi as GridApi);

    const buttonDom = document.getElementById(domId);
    if (buttonDom) {
      buttonDom.scrollIntoView(true);
      await delay(5000);
    }

    const fileName = getFileName();
    const csv = gridApiToUse.getDataAsCsv({
      fileName,
      processCellCallback: processCellCallback({
        formatValue,
        formatField,
        formatMetric,
      }),
    });

    if (csv) {
      return {
        content: csv.replace(/€/g, ''),
        fileName,
      };
    } else {
      window.alert('Something went wrong!');
      throw new Error(`Failed to export ${fileName}`);
    }
  }, [
    gridApi,
    overrideGridApi,
    domId,
    getFileName,
    formatValue,
    formatField,
    formatMetric,
  ]);

  return { exportDataAsCsv, getFileName, domId };
};

export default useGridCsvExport;
